import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PASSWORD_HINT } from '@trellis/shared';
import { Button, Card, Field, Input, PasswordInput } from '../components/ui';

/**
 * Cambio de contraseña obligatorio en el primer ingreso.
 *
 * Las credenciales de un paciente las genera la institución y viajan por mail o
 * se dictan por teléfono. Forzar el cambio es lo que evita que esa clave inicial
 * quede en uso indefinidamente.
 */
export function ChangePassword() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, changePassword } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!user) return <Navigate to={`/${slug}`} replace />;

  const primerIngreso = user.mustChangePassword;
  const tooShort = next.length > 0 && next.length < 10;
  const mismatch = confirm.length > 0 && next !== confirm;

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (next !== confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(current, next);
      // Confirmación explícita antes de seguir. Cambiar una contraseña y que la
      // pantalla simplemente cambie deja la duda de si se guardó, y quien duda
      // vuelve a cambiarla o llama al equipo de seguimiento.
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No pudimos cambiar tu contraseña');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center px-5 py-10 bg-surface">
        <div className="w-full max-w-sm text-center">
          <div className="size-14 rounded-full bg-success-soft text-success grid place-items-center mx-auto">
            <Check className="size-7" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mt-5">Listo, ya está cambiada</h1>
          <p className="text-fg-muted mt-2 text-[15px] leading-relaxed">
            De ahora en más entrá con tu contraseña nueva. Si habías iniciado sesión en otro
            dispositivo, ahí vas a tener que entrar de nuevo.
          </p>
          <Button
            className="mt-6"
            size="lg"
            fullWidth
            onClick={() => navigate(`/${slug}`, { replace: true })}
          >
            Continuar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center px-5 py-10 bg-surface">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold tracking-tight text-center">
          {primerIngreso ? 'Creá tu contraseña' : 'Cambiar tu contraseña'}
        </h1>
        <p className="text-fg-muted text-center mt-2 text-[15px]">
          {primerIngreso
            ? 'Por seguridad, reemplazá la contraseña que te dieron por una tuya.'
            : 'Elegí una contraseña nueva para tu cuenta.'}
        </p>

        <Card className="p-6 mt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* En el primer ingreso no se pide la contraseña actual: la persona
                acaba de usarla para entrar y preguntársela solo confunde
                —"¿cuál es mi contraseña actual si nunca tuve una?"—. En el
                cambio voluntario sí se pide, y ahí protege de que alguien que
                encontró la sesión abierta se apropie de la cuenta. */}
            {!primerIngreso && (
              <Field label="Contraseña actual" htmlFor="current">
                <PasswordInput
                  id="current"
                  autoComplete="current-password"
                  value={current}
                  onChange={(event) => setCurrent(event.target.value)}
                  required
                />
              </Field>
            )}

            <Field
              label="Contraseña nueva"
              htmlFor="next"
              hint={PASSWORD_HINT}
              error={tooShort ? 'Le faltan caracteres' : undefined}
            >
              <PasswordInput
                id="next"
                autoComplete="new-password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
                required
                minLength={10}
              />
            </Field>

            <Field
              label="Repetí la contraseña nueva"
              htmlFor="confirm"
              error={mismatch ? 'No coinciden' : undefined}
            >
              <PasswordInput
                id="confirm"
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                required
              />
            </Field>

            {error && (
              <p className="text-sm text-danger" role="alert">
                {error}
              </p>
            )}

            <Button
              type="submit"
              loading={submitting}
              disabled={tooShort || mismatch || !next}
              fullWidth
              size="lg"
            >
              Guardar y continuar
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
